import { useMemo } from 'react';

import { EMetric, metricDictionary, StatsResult } from 'api/useAnalytics';
import Decimal from 'decimal.js';
import { EStatisticType } from 'views/Statistics/StatisticsView.types';

import { getFormattedValueByType } from '../../utils';
import { KPI_COLUMN } from '../constants';

type AttributeValue = string;

interface KPIData {
  [KPI_COLUMN.accessor]: string;
  metricName: EMetric;
  [key: AttributeValue]: number | string; 
}

interface UseStatsTableDataProps {
  attributeValues: AttributeValue[];
  statsData: Record<AttributeValue, StatsResult[]>; 
  metrics: EMetric[];
}

const formatMetricValue = (rawValue: number, metricName: EMetric): string => {
  const metricMetaData = metricDictionary[metricName];
  const metricType = metricMetaData?.type ?? EStatisticType.NUMBER;
  
  const processedValue = metricType === EStatisticType.AMOUNT_AVERAGE || metricType === EStatisticType.CURRENCY
    ? new Decimal(rawValue).div(100).toNumber() 
    : rawValue;
  
  return getFormattedValueByType(processedValue, metricType);
};

export const useStatsTableData = ({ 
  attributeValues, 
  statsData, 
  metrics 
}: UseStatsTableDataProps) => {
  return useMemo(() => {
    return metrics.map((metric) => {
      const row: KPIData = {
        [KPI_COLUMN.accessor]: metricDictionary[metric]?.title || metric,
        metricName: metric
      };

      attributeValues.forEach((attributeValue) => {
        const statsForAttribute = statsData[attributeValue];
        const statForMetric = statsForAttribute?.find(stat => stat.name === metric);
        const rawValue = statForMetric?.currentValue || 0;
        
        row[attributeValue] = formatMetricValue(rawValue, metric);
      });

      return row;
    });
  }, [attributeValues, metrics, statsData]);
};

export type { KPIData }; 